/*
 *  Copyright IBM Corp. 2025, 2026
 *
 *  This source code is licensed under the Apache-2.0 license found in the
 *  LICENSE file in the root directory of this source tree.
 *
 *  @license
 */

import CDSButton from '@carbon/web-components/es/components/button/button.js';
import AiLaunch24 from '@carbon/icons/es/ai-launch/24.js';
import ChatLaunch24 from '@carbon/icons/es/chat--launch/24.js';
import Close16 from '@carbon/icons/es/close/16.js';
import { carbonIconToReact } from '../../utils/carbonIcon';
import cx from 'classnames';
import React, {
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import { usePrevious } from '../../hooks/usePrevious';
import Button, {
  BUTTON_KIND,
  BUTTON_SIZE,
  BUTTON_TOOLTIP_POSITION,
  BUTTON_TYPE,
} from '../../components/carbon/Button';
import { doFocusRef, isDirectionRTL } from '../../utils/domUtils';
import { prefersReducedMotion } from '../../utils/prefersReducedMotion';
import { HasRequestFocus } from '../../../types/utilities/HasRequestFocus';
import { uuid } from '@carbon/ai-chat-components/es/globals/utils/uuid.js';

// Upper bound for the launcher extended open/close animations
// (`$duration-moderate-01` = 150ms, plus a fade stage) with a generous buffer.
// Used as a safety net in case `animationend` never fires — most often under
// `prefers-reduced-motion: reduce`, where the CSS animation is never declared.
const LAUNCHER_ANIMATION_TIMEOUT_MS = 600;

const AiLaunch = carbonIconToReact(AiLaunch24);
const ChatLaunch = carbonIconToReact(ChatLaunch24);
const CloseIcon = carbonIconToReact(Close16);

export enum LauncherOpenState {
  Opening = 'opening',
  Open = 'open',
  Closing = 'closing',
  Closed = 'closed',
}

type LauncherHandle = HasRequestFocus & {
  buttonElement: () => CDSButton | undefined;
  containerElement: () => HTMLDivElement | undefined;
  launcherContainerElement?: () => HTMLDivElement | undefined;
};

interface LauncherProps {
  /**
   * Necessary to get access to the ref created within App.tsx.
   */
  launcherRef: RefObject<LauncherHandle | null>;
  onToggleOpen: () => void;
  onClose: () => void;
  launcherHidden: boolean;
  extended: boolean;
  showUnreadIndicator: boolean;
  unreadMessageCount: number;
  mainWindowOpen: boolean;
  launcherGreetingMessage: string;
  launcherAvatarUrl?: string;
  closeButtonLabel: string;
  closedLabel: string;
  openLabel: string;
  aiEnabled: boolean;
  formatUnreadMessageLabel?: ({ count }: { count: number }) => string;
  dataTestId?: string;
}

// Stable id for focus target of the skip link
const launcherButtonId = `cds-aichat-launcher-button-${uuid()}`;

function Launcher(props: LauncherProps) {
  const {
    launcherRef,
    onToggleOpen,
    onClose,
    launcherHidden,
    extended,
    showUnreadIndicator,
    unreadMessageCount,
    mainWindowOpen,
    launcherGreetingMessage,
    launcherAvatarUrl,
    closeButtonLabel,
    closedLabel,
    openLabel,
    aiEnabled,
    formatUnreadMessageLabel,
    dataTestId,
  } = props;

  const prevExtended = usePrevious(extended);

  const [callToActionOpenState, setCallToActionOpenState] =
    useState<LauncherOpenState>(() =>
      extended ? LauncherOpenState.Open : LauncherOpenState.Closed
    );

  const textHolderRef = useRef<HTMLDivElement | null>(null);
  const greetingMessageRef = useRef<HTMLDivElement | null>(null);
  const extendedContainerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<CDSButton | null>(null);

  const tooltipPosition = isDirectionRTL()
    ? BUTTON_TOOLTIP_POSITION.RIGHT
    : BUTTON_TOOLTIP_POSITION.LEFT;

  const ariaLabelSuffix =
    unreadMessageCount !== 0
      ? formatUnreadMessageLabel?.({ count: unreadMessageCount })
      : undefined;

  const launcherAriaLabel = [
    launcherHidden ? openLabel : closedLabel,
    ariaLabelSuffix,
  ]
    .filter(Boolean)
    .join('. ');

  const launcherAvatar = launcherAvatarUrl ? (
    // eslint-disable-next-line jsx-a11y/alt-text
    <img
      className="cds-aichat--launcher__avatar"
      src={launcherAvatarUrl}
      aria-hidden
      alt=""
    />
  ) : aiEnabled ? (
    <AiLaunch
      className="cds-aichat--launcher__svg"
      aria-label={launcherAriaLabel}
      role="img"
    />
  ) : (
    <ChatLaunch
      className="cds-aichat--launcher__svg"
      aria-label={launcherAriaLabel}
      role="img"
    />
  );

  const reduceLauncher = useCallback(() => {
    if (extended) {
      onClose();
    }
  }, [extended, onClose]);

  // If the main window has been opened then clear all timers and set the launcher state as if it had been
  // clicked open. This is to protect against scenarios where the main window is opened using other methods besides
  // clicking on the launcher.
  useEffect(() => {
    if (mainWindowOpen) {
      // Clear timers and update launcher state so that no more greeting messages are queued.
      reduceLauncher();
    }
  }, [mainWindowOpen, reduceLauncher]);

  const handleToggleOpen = useCallback(() => {
    reduceLauncher();
    onToggleOpen();
  }, [onToggleOpen, reduceLauncher]);

  const handleDismiss = useCallback(() => {
    reduceLauncher();
  }, [reduceLauncher]);

  const handleButtonRef = useCallback((element: CDSButton | null) => {
    buttonRef.current = element ?? null;
  }, []);

  const buttonTabIndex = launcherHidden ? -1 : undefined;

  useImperativeHandle(launcherRef, () => ({
    requestFocus: () => {
      doFocusRef(buttonRef);
    },
    buttonElement: () => buttonRef.current ?? undefined,
    containerElement: () => extendedContainerRef.current ?? undefined,
    launcherContainerElement: () => extendedContainerRef.current ?? undefined,
  }));

  // React to changes in the "extended" prop: trigger opening/closing transitions.
  useEffect(() => {
    // Skip first render if usePrevious returns undefined initially
    if (prevExtended !== undefined) {
      if (!prevExtended && extended) {
        setCallToActionOpenState(LauncherOpenState.Opening);
      } else if (prevExtended && !extended) {
        setCallToActionOpenState(LauncherOpenState.Closing);
      }
    }
  }, [extended, prevExtended]);

  // When opening animation on greeting completes, transition Opening -> Open
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    if (callToActionOpenState === LauncherOpenState.Opening) {
      const element = greetingMessageRef.current;

      // Snap straight to Open when the element is missing or when reduced
      // motion is active (the CSS animation is not declared, so `animationend`
      // will never fire — see Launcher.scss `prefers-reduced-motion` gating).
      if (!element || prefersReducedMotion()) {
        setCallToActionOpenState(LauncherOpenState.Open);
      } else {
        const handleAnimationEnd = (event: AnimationEvent) => {
          if (event.target === element) {
            setCallToActionOpenState(LauncherOpenState.Open);
          }
        };

        element.addEventListener('animationend', handleAnimationEnd);
        element.addEventListener('animationcancel', handleAnimationEnd);
        // Safety net: if `animationend` is never dispatched (e.g. element
        // re-parented, display hidden mid-animation, future CSS edit drops
        // the animation), still advance the state machine.
        const timeoutId = setTimeout(() => {
          setCallToActionOpenState(LauncherOpenState.Open);
        }, LAUNCHER_ANIMATION_TIMEOUT_MS);

        cleanup = () => {
          element.removeEventListener('animationend', handleAnimationEnd);
          element.removeEventListener('animationcancel', handleAnimationEnd);
          clearTimeout(timeoutId);
        };
      }
    }

    return cleanup;
  }, [callToActionOpenState]);

  // When closing animation on text holder completes, transition Closing -> Closed
  useEffect(() => {
    let cleanup: (() => void) | undefined;

    if (callToActionOpenState === LauncherOpenState.Closing) {
      const element = textHolderRef.current;

      // See Opening branch above — same rationale for the reduced-motion and
      // null-element short-circuits.
      if (!element || prefersReducedMotion()) {
        setCallToActionOpenState(LauncherOpenState.Closed);
      } else {
        const handleAnimationEnd = (event: AnimationEvent) => {
          if (event.target === element) {
            setCallToActionOpenState(LauncherOpenState.Closed);
          }
        };

        element.addEventListener('animationend', handleAnimationEnd);
        element.addEventListener('animationcancel', handleAnimationEnd);
        const timeoutId = setTimeout(() => {
          setCallToActionOpenState(LauncherOpenState.Closed);
        }, LAUNCHER_ANIMATION_TIMEOUT_MS);

        cleanup = () => {
          element.removeEventListener('animationend', handleAnimationEnd);
          element.removeEventListener('animationcancel', handleAnimationEnd);
          clearTimeout(timeoutId);
        };
      }
    }

    return cleanup;
  }, [callToActionOpenState]);

  const shouldShowGreeting =
    callToActionOpenState === LauncherOpenState.Opening ||
    callToActionOpenState === LauncherOpenState.Open;

  return (
    <div
      ref={extendedContainerRef}
      className={cx('cds-aichat-launcher', {
        'cds-aichat-launcher--opening':
          callToActionOpenState === LauncherOpenState.Opening,
        'cds-aichat-launcher--open':
          callToActionOpenState === LauncherOpenState.Open,
        'cds-aichat-launcher--closing':
          callToActionOpenState === LauncherOpenState.Closing,
        'cds-aichat-launcher--closed':
          callToActionOpenState === LauncherOpenState.Closed,
        'cds-aichat-launcher--hidden': launcherHidden,
      })}>
      <Button
        className="cds-aichat--launcher-extended__close-button"
        kind={BUTTON_KIND.SECONDARY}
        size={BUTTON_SIZE.EXTRA_SMALL}
        aria-label={closeButtonLabel}
        onClick={handleDismiss}
        tooltipPosition={tooltipPosition}
        tooltip-text={closeButtonLabel}>
        <CloseIcon aria-label={closeButtonLabel} slot="icon" />
      </Button>
      <Button
        role="complementary"
        id={launcherButtonId}
        aria-label={launcherAriaLabel}
        tooltip-text={launcherAriaLabel}
        className="cds-aichat--launcher__button"
        data-testid={dataTestId}
        kind={BUTTON_KIND.PRIMARY}
        onClick={handleToggleOpen}
        ref={handleButtonRef}
        tabIndex={buttonTabIndex}
        type={BUTTON_TYPE.BUTTON}
        tooltipPosition={tooltipPosition}>
        <div className="cds-aichat--launcher__wrapper">
          <div
            className="cds-aichat--launcher-extended__text-holder"
            ref={textHolderRef}>
            <div
              className="cds-aichat--launcher-extended__greeting"
              ref={greetingMessageRef}>
              {shouldShowGreeting && (
                <div className="cds-aichat--launcher-extended__greeting-text">
                  {launcherGreetingMessage}
                </div>
              )}
            </div>
          </div>
          <div className="cds-aichat--launcher__icon-holder">
            {launcherAvatar}
          </div>
        </div>
        {(unreadMessageCount !== 0 || showUnreadIndicator) && (
          <div className="cds-aichat--count-indicator">
            {unreadMessageCount !== 0 ? unreadMessageCount : ''}
          </div>
        )}
      </Button>
    </div>
  );
}

export type { LauncherHandle };
export { Launcher };
